import axios from 'axios';
import { API_BASE_URL, createAuthHeader } from '../config/api.config';

/**
 * Fetches filter options for machines page from backend API
 * @param {string} customerId - Customer ID for filtering
 * @returns {Promise<Object>} Hierarchical filter structure with groups, clusters, locations
 */
export const fetchMachineFilterOptions = async (customerId) => {
    const token = localStorage.getItem('access_token');
    const response = await axios.get(`${API_BASE_URL}/filters?clientId=${customerId}`, {
      headers: createAuthHeader(token)
    });
    
    return response.data;
};

/**
 * Fetches machines data from backend API with optional filtering
 * @param {Object} filters - Filter options (group, location)
 * @param {string} customerId - Customer ID for filtering
 * @returns {Promise<Array>} Array of machine objects with slots and stock information
 */
/**
 * Fetches units and revenue per month over a product's whole history at a store
 */
export const fetchSlotMonthlySales = async (clientId, storeId, productId) => {
  const token = localStorage.getItem('access_token');
  const params = new URLSearchParams({ clientId });
  const response = await axios.get(
    `${API_BASE_URL}/machines/${storeId}/products/${productId}/monthly-sales?${params.toString()}`,
    { headers: createAuthHeader(token) }
  );
  return response.data;
};

/**
 * Fetches one unit of a product at a store today: the lane's planogram price, the last sale's price,
 * the base cost and each tax/fee by the product tag that charges it
 */
export const fetchSlotUnitEconomics = async (clientId, storeId, productId, row, column) => {
  const token = localStorage.getItem('access_token');
  const params = new URLSearchParams({ clientId });
  if (row != null && column != null) {
    params.set('row', row);
    params.set('column', column);
  }
  const response = await axios.get(
    `${API_BASE_URL}/machines/${storeId}/products/${productId}/unit-economics?${params.toString()}`,
    { headers: createAuthHeader(token) }
  );
  return response.data;
};

/**
 * Fetches the latest individual sales of a machine, optionally only for one product
 */
export const fetchRecentSales = async (clientId, storeId, productId, limit = 20) => {
  const token = localStorage.getItem('access_token');
  const params = new URLSearchParams({ clientId, limit: String(limit) });
  if (productId) params.append('productId', String(productId));
  const response = await axios.get(
    `${API_BASE_URL}/machines/${storeId}/recent-sales?${params.toString()}`,
    { headers: createAuthHeader(token) }
  );
  return response.data;
};

export const lookupMachineByNumber = async (clientId, storeId) => {
  const token = localStorage.getItem('access_token');
  const response = await axios.get(
    `${API_BASE_URL}/machines/lookup?clientId=${clientId}&storeId=${storeId}`,
    { headers: createAuthHeader(token) }
  );
  return response.data;
};

export const fetchMachinesData = async (filters = {}, customerId = null) => {
  try {
    const token = localStorage.getItem('access_token');
    
    // Build query parameters
    const params = new URLSearchParams();
    
    // Add clientId - use customerId parameter if provided, otherwise check filters
    const clientId = customerId || filters.clientId;
    if (clientId) {
      params.append('clientId', clientId);
    }
    
    if (filters.group) {
      params.append('group', filters.group);
    }
    if (filters.location) {
      params.append('location', filters.location);
    }
    if (filters.period) {
      params.append('period', String(filters.period));
    }

    const url = `${API_BASE_URL}/machines${params.toString() ? '?' + params.toString() : ''}`;
    
    const response = await axios.get(url, {
      headers: createAuthHeader(token)
    });
    
    return response.data;
  } catch (error) {
    return []; // Return empty array on error
  }
};
